import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getReadinessLatest,
  getReadinessRunStatus,
  getReadinessSnapshot,
  runReadinessAssessment,
} from '../../services/orderXApi';
import {
  formatReadinessError,
  isTransientStatusMessage,
  reportFromSnapshot,
  resolveOperatorUserId,
} from './readinessUtils';

const DEFAULT_POLL_MS = 3000;
// After this many consecutive failed reads we surface a soft warning, but keep
// polling and keep the live progress UI on screen — the assessment continues
// server-side, so a slow/failed status read must not abort the UI.
const MAX_CONSECUTIVE_POLL_FAILURES = 3;
// Only treat the run as genuinely lost (fatal error) after status reads have
// failed continuously for this long. A single heavy check can block the
// backend event loop for a while; we ride that out instead of giving up.
const LOST_CONNECTION_MS = 120000;

/**
 * Drives an async readiness assessment: kicks off the background run, then polls
 * `run/status` resiliently until a terminal state, and loads the resulting
 * snapshot. Shared by the Dashboard, Wizard, and Symptom Triage screens.
 */
export function useReadinessRun({ pollIntervalMs = DEFAULT_POLL_MS, onComplete } = {}) {
  const [running, setRunning] = useState(false);
  const [runStartedAt, setRunStartedAt] = useState('');
  const [report, setReport] = useState(null);
  const [snapshotMeta, setSnapshotMeta] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [pollWarning, setPollWarning] = useState('');
  const [progress, setProgress] = useState(null);

  const timerRef = useRef(null);
  const pollingRef = useRef(false);
  const failureCountRef = useRef(0);
  const firstFailureAtRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const stopPolling = useCallback(() => {
    pollingRef.current = false;
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const loadLatest = useCallback(async () => {
    const snapshot = await getReadinessLatest();
    if (!snapshot) {
      setReport(null);
      setSnapshotMeta(null);
      return null;
    }
    setSnapshotMeta(snapshot);
    setReport(reportFromSnapshot(snapshot));
    return snapshot;
  }, []);

  const loadCompletedReport = useCallback(
    async (snapshotId) => {
      if (snapshotId != null) {
        try {
          const snapshot = await getReadinessSnapshot(snapshotId);
          if (snapshot) {
            setSnapshotMeta(snapshot);
            setReport(reportFromSnapshot(snapshot));
            return;
          }
        } catch (_err) {
          // Fall back to the latest snapshot below.
        }
      }
      await loadLatest();
    },
    [loadLatest]
  );

  const finishRun = useCallback(
    async (statusPayload) => {
      stopPolling();
      failureCountRef.current = 0;
      firstFailureAtRef.current = 0;
      setPollWarning('');
      setRunning(false);
      setRunStartedAt('');
      setProgress(null);
      if (statusPayload?.status === 'failed') {
        setInfo('');
        setError(formatReadinessError(statusPayload?.error) || 'Background assessment failed.');
        return;
      }
      try {
        await loadCompletedReport(statusPayload?.snapshot_id);
        setInfo('Assessment completed.');
      } catch (err) {
        setError(err?.message || 'Assessment completed but the report could not be loaded.');
        return;
      }
      if (typeof onCompleteRef.current === 'function') {
        onCompleteRef.current(statusPayload);
      }
    },
    [loadCompletedReport, stopPolling]
  );

  const poll = useCallback(async () => {
    try {
      const statusPayload = await getReadinessRunStatus();
      failureCountRef.current = 0;
      firstFailureAtRef.current = 0;
      setPollWarning('');
      const status = statusPayload?.status;
      if (status === 'running') {
        setRunning(true);
        if (statusPayload.started_at) {
          setRunStartedAt(statusPayload.started_at);
        }
        if (statusPayload.progress) {
          setProgress(statusPayload.progress);
        }
        return;
      }
      if (status === 'complete' || status === 'failed' || status === 'idle') {
        await finishRun(statusPayload);
      }
    } catch (err) {
      failureCountRef.current += 1;
      if (firstFailureAtRef.current === 0) {
        firstFailureAtRef.current = Date.now();
      }
      const downForMs = Date.now() - firstFailureAtRef.current;
      // Only give up (and tear down the live progress UI) once status reads have
      // failed continuously for a long stretch — a real outage, not one slow
      // check. Until then we keep `running`/`progress` intact and keep polling.
      if (downForMs >= LOST_CONNECTION_MS) {
        stopPolling();
        setRunning(false);
        setProgress(null);
        setPollWarning('');
        setError(err?.message || 'Unable to check assessment status.');
        return;
      }
      if (failureCountRef.current >= MAX_CONSECUTIVE_POLL_FAILURES) {
        setPollWarning(
          isTransientStatusMessage(err?.message)
            ? 'A check is taking a while — status updates are delayed but the assessment is still running…'
            : 'Temporary problem checking status; still retrying…'
        );
      }
    }
  }, [finishRun, stopPolling]);

  const pollRef = useRef(poll);
  pollRef.current = poll;

  // Self-scheduling poll loop: the next status read is only queued AFTER the
  // previous one settles. This prevents a burst of overlapping requests (and a
  // cascade of false timeouts) when a single read is slow.
  const startPolling = useCallback(() => {
    stopPolling();
    failureCountRef.current = 0;
    firstFailureAtRef.current = 0;
    pollingRef.current = true;
    const tick = async () => {
      if (!pollingRef.current) {
        return;
      }
      await pollRef.current();
      if (pollingRef.current) {
        timerRef.current = window.setTimeout(tick, pollIntervalMs);
      }
    };
    tick();
  }, [pollIntervalMs, stopPolling]);

  const startRun = useCallback(
    async (payload = {}) => {
      // Wipe the previous assessment off the screen so the new run paints fresh
      // instead of leaving stale results behind the progress card.
      setError('');
      setPollWarning('');
      setProgress(null);
      setReport(null);
      setSnapshotMeta(null);
      failureCountRef.current = 0;
      firstFailureAtRef.current = 0;
      setRunning(true);
      setInfo('Assessment running in background…');
      setRunStartedAt(new Date().toISOString());
      try {
        const data = await runReadinessAssessment({
          ...payload,
          async_run: true,
          operator_user_id: payload.operator_user_id ?? (resolveOperatorUserId() || undefined),
        });
        if (data?.httpStatus === 202 || data?.status === 'running') {
          if (data.started_at) {
            setRunStartedAt(data.started_at);
          }
          startPolling();
          return;
        }
        setReport(data);
        setSnapshotMeta(null);
        setRunning(false);
        setInfo('Assessment completed.');
      } catch (err) {
        setRunning(false);
        setRunStartedAt('');
        setInfo('');
        setError(err?.message || 'Assessment failed.');
      }
    },
    [startPolling]
  );

  const resumeRun = useCallback(
    (statusPayload) => {
      if (statusPayload?.status !== 'running') {
        return;
      }
      setRunning(true);
      if (statusPayload.started_at) {
        setRunStartedAt(statusPayload.started_at);
      }
      if (statusPayload.progress) {
        setProgress(statusPayload.progress);
      }
      setInfo('Assessment running in background…');
      startPolling();
    },
    [startPolling]
  );

  useEffect(() => stopPolling, [stopPolling]);

  return {
    running,
    runStartedAt,
    report,
    snapshotMeta,
    error,
    info,
    pollWarning,
    progress,
    setReport,
    setSnapshotMeta,
    setError,
    setInfo,
    startRun,
    resumeRun,
    loadLatest,
    stopPolling,
  };
}
